
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Params } from '@angular/router';
import { NgForm } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';

import srtParser2 from 'srt-parser-2';
import Parser, { Line } from 'srt-parser-2'; // types

import { AbstractDetailsComponent } from 'app/components/shared/abstract-details.component';
import { AssetService } from 'app/services/asset.service';
import { SubtitleService } from 'app/services/subtitle.service';
import srtValidator from 'srt-validator';

import { LineWithMessage } from 'app/components/shared/bb-code-editor/bb-code-editor.component';
import { Mediaclip, MediaclipSubtitle } from 'app/interfaces/mediaclip';
import { SapiEntity } from 'app/services/sapi.service';
import { Throttle } from 'app/decorators/throttle.decorator';
import { Debounce } from 'app/decorators/debounce.decorator';
import { BasicPlayerComponent } from 'app/components/shared/basic-player/basic-player.component';
import { BbSubtitleLineEditorComponent } from 'app/components/shared/bb-subtitle-line-editor/bb-subtitle-line-editor.component';
import { BBPlayerAPI } from 'app/interfaces/bb-player-api';

export interface Subtitle extends SapiEntity, MediaclipSubtitle {
	srt: string;
	mediaclipid?: string;
}

export interface LanguageItem {
	countrycode?: string;
	id: number;
	isocode: string;
	name: string;
}

@Component({
	selector: 'subtitle-quickedit',
	templateUrl: './subtitle-quickedit.component.html',
	styleUrls: ['./subtitle-quickedit.component.scss']
})
export class SubtitleQuickEditComponent extends AbstractDetailsComponent implements OnInit, OnDestroy {
	@ViewChild('formRef', { static: true }) form: NgForm;
	@ViewChild('lineEditor', { static: true }) lineEditorRef: BbSubtitleLineEditorComponent;
	@ViewChild('player', { static: false }) set bp(player) {
		this.basicPlayer = player;
	}

	subtitle: Subtitle;
	languages: LanguageItem[] = [];
	basicPlayer: BasicPlayerComponent;
	currentTime: number; // in player

	languageOptions: { value: string; label: string }[] = [];
	entity: Subtitle;

	validator = srtValidator;
	subscriptions = new Subscription();

	mediaclip: Mediaclip;

	currentMode: 'text' | 'editor' | 'loading' = 'loading';

	errorMessage: string;

	// mode = editor
	inViewSubtitleId: string;
	startId: string;
	endId: string;
	srtParser: Parser = new srtParser2();
	srtObject: Line[];
	hoverCanSetInput: 'start' | 'end';

	// mode = text
	highlightLineNumber: number;
	errorLineNumbers: LineWithMessage[];

	constructor(
		protected route: ActivatedRoute,
		protected location: Location,
		private assetService: AssetService,
		private subtitleService: SubtitleService
	) {
		super(route, location);

		// this will load the subtitle on init
		this.entityConfiguration = {
			name: 'subtitle',
			entityTranslation: this.translate.instant('SUBTITLE'),
			parentPath: '/library/mediaclips',
			basePath: '',
			defaults: {
				srt: '1\n00:00:01,000 --> 00:00:05,000\nSample',
				status: 'published',
				isocode: ''
			}
		};
	}

	ngOnInit() {
		super.ngOnInit();

		this.subscriptions.add(this.subtitleService.languagesSubject.subscribe(langOptions => {
			this.languages = langOptions;
			this.setLanguageOptions();
		}));

		this.subscriptions.add(this.entityLoaded.subscribe(() => {
			this.initializeExisting();
		}));

		if (this.isNew) {
			this.initializeNew();
		}

		// get mediaclip
		this.subscriptions.add(this.route.params.subscribe((params: Params) => {
			this.fetchMediaClip(params.parentid);
		}));

		this.subscriptions.add(this.subtitleService.currentTimeSubject.subscribe(seconds => {
			this.currentTime = seconds;
		}));
	}

	ngOnDestroy() {
		if (this.subscriptions) {
			this.subscriptions.unsubscribe();
		}
	}

	save(force: boolean): Observable<void> {
		// make sure data for both sources is synced (for error handling)
		if (this.currentMode === 'editor') {
			this.setEntitySrt(this.srtParser.toSrt(this.srtObject));
		}
		if (this.currentMode === 'text') {
			this.srtObject = this.srtParser.fromSrt(this.entity.srt);
		}

		if (!this.checkIsValidSrt()) {
			return;
		}

		const saveSubject = super.save(force, null, false);
		saveSubject.subscribe(() => {
			this.onIsocodeChange(this.entity.isocode);
			this.subtitleService.subtitleChanged(this.entity);
		});
		return saveSubject;
	}

	private initializeNew(): void {
		this.entity.isocode = ''; // somehow defaults to 'en', reset
		this.setMode('editor');
		this.setEntitySrt(this.entity.srt); // set for initial preview subtitle
		this.form.form.markAsDirty();

		// only for new: check if you uploaded an .srt file before opening modal
		this.subscriptions.add(this.subtitleService.uploadedSrtSubject.subscribe((srt: string) => {
			if (srt) {
				this.setEntitySrt(srt);
				this.subtitleService.setUploadedSrt(undefined); // reset
				this.form.form.markAsDirty();
				setTimeout(() => {
					this.setMode('editor');
				});
			}
		}));
	}

	private initializeExisting(): void {
		this.resetErrors();
		// reset to 0 (when editing and pressing CANCEL) to update state buttons
		this.subtitleService.seekTo(0);

		// get the .srt for existing on load and on cancel (not part of the generic entity response)
		if (!this.isNew) {
			this.sapiService.getUrl('/subtitle/' + this.activeId + '/download', null, { responseType: 'text' }).subscribe((srt: string) => {
				this.setEntitySrt(srt);
				this.setMode(this.currentMode !== 'loading' ? this.currentMode : 'editor');
			});
		}
	}

	private fetchMediaClip(clipiId: string): void {
		if (this.isNew) {
			// required for saving new
			this.entity.mediaclipid = clipiId;
			// set redirect for after save (url subtitle/new -> subtitle/:id)
			this.entityConfiguration.basePath = `mediaclip/${clipiId}/subtitle`;
		}

		this.sapiService.get('commonclip', clipiId).subscribe((mediaclip: Mediaclip) => {
			this.mediaclip = mediaclip;
			this.setLanguageOptions();
		});
	}

	onPlayerCreated(api: BBPlayerAPI) {
		this.subtitleService.setEditorPlayer(api);
	}

	private setEntitySrt(srt: string): void {
		this.entity.srt = srt;

		// update player preview
		this.subtitleService.setPreviewSubtitle(this.entity.srt);
	}

	handleSrtFile(file: File) {
		const reader = new FileReader();
		reader.onload = (e: any) => {
			this.setEntitySrt(e.target.result);
			setTimeout(() => {
				this.setMode('editor');
				this.form.form.markAsDirty();
			});
		};

		reader.readAsText(file);
	}

	onFileDropped(fileList: FileList) {
		this.handleSrtFile(fileList[0]);
	}

	// user uploads file in modal
	onFileChanged(ev: Event) {
		const inputEl = (ev.target as HTMLInputElement);
		const file = inputEl.files[0];
		this.handleSrtFile(file);
		inputEl.value = '';
	}

	onIsocodeChange(isocode: string) {
		const lang = this.languages.find((language: LanguageItem) => language.isocode === isocode);
		if (lang && lang.id) {
			this.entity.languageid = lang.id;
			this.entity.languagename = lang.name;
		}
	}

	seekFrame(frameOffset: number) {
		const frameRate = 25;
		const frameTime = 1 / frameRate;
		const time = this.currentTime + frameOffset * frameTime;
		this.subtitleService.seekTo(time);
	}

	updateSubtitleLineTime(id: string, position: 'start' | 'end'): void {
		if (this.lineEditorRef) {
			this.lineEditorRef.updateLineTime(id, position, this.currentTime);
		}
	}

	addSubtitle(previousLineId: string): void {
		if (this.lineEditorRef) {
			this.lineEditorRef.addLine(previousLineId, this.currentTime);
		}
	}

	@Throttle(500)
	onSrtChanged() {
		// sync highlight line with new srt
		if (this.currentTime) {
			this.setLineHighlightBasedOnCurrentTime();
		}
	}

	@Debounce(1000)
	onSrtChangedDebounced() {
		this.checkIsValidSrt();
	}

	// update .srt for preview in video
	@Debounce(500)
	editorObjectChanged() {
		this.setEntitySrt(this.srtParser.toSrt(this.srtObject));
	}

	setMode(mode: 'editor' | 'text'): void {
		let newMode = mode;

		if (mode === 'editor') {
			const valid = this.checkIsValidSrt();

			if (!valid) {
				newMode = 'text';
				this.srtObject = this.srtParser.fromSrt(this.entity.srt);
			}
		}

		if (newMode === 'editor') {
			this.srtObject = this.srtParser.fromSrt(this.entity.srt);
		}

		if (this.currentMode === 'editor' && newMode === 'text') {
			this.setEntitySrt(this.srtParser.toSrt(this.srtObject));
		}

		this.currentMode = newMode;

		if (this.currentMode === 'editor' && newMode === 'text') {
			this.setLineHighlightBasedOnCurrentTime();
		}
	}

	setHoverHighlight(field?: 'start' | 'end'): void {
		this.subtitleService.setHighlightCanSetInput(field);
	}

	// note checked performance with 1.5h .srt: takes 1-5 ms each run
	@Throttle(200)
	setLineHighlightBasedOnCurrentTime() {
		if (this.currentMode === 'editor') {
			return;
		}

		// subtitle timestamps, e.g. 00:00:05,460 --> 00:00:12,460
		const timestamp = /(\d{2}:\d{2}:\d{2},\d{3})/gm;
		const timestampArray = this.entity.srt.match(timestamp);

		if (!timestampArray || timestampArray?.length === 0) {
			if (this.highlightLineNumber) {
				this.highlightLineNumber = undefined;
			}
			return;
		}

		const secondsArray = timestampArray.map((t: string) => {
			const hours = parseInt(t.slice(0, 2), 10);
			const minutes = parseInt(t.slice(3, 5), 10);
			const seconds = parseInt(t.slice(6, 8), 10);
			const ms = parseInt(t.slice(9, 12), 10);

			return { timestamp: t, seconds: seconds + minutes * 60 + hours * 60 * 60 + ms / 1000 };
		});

		const rangeArray: { fromTimestamp: string; from: number; to: number }[] = [];
		for (let i = 0; i < secondsArray.length; i = i + 2) {
			rangeArray.push({
				fromTimestamp: secondsArray[i].timestamp,
				from: secondsArray[i].seconds,
				to: secondsArray[i + 1] ? secondsArray[i + 1].seconds : secondsArray[i].seconds // edge case end time missing
			});
		}

		// time inbetween one of those values
		const result = rangeArray.find(range => this.currentTime >= range.from && this.currentTime <= range.to);

		if (!result) {
			if (this.highlightLineNumber) {
				this.highlightLineNumber = undefined;
			}
			return;
		}

		// get line number, based on number of line breaks (\n) found in the srt before this timestamp
		// take last result (in case end time is equal to next start time)
		const index = this.entity.srt.lastIndexOf(result.fromTimestamp);
		const nrBreaks = this.entity.srt.slice(0, index).match(/\n/gm);
		const line = nrBreaks.length + 1;

		this.highlightLineNumber = line;
	}

	private setLanguageOptions(): void {
		if (!this.languages?.length) {
			return;
		}

		this.languageOptions = this.languages.map((language: LanguageItem) => ({
			value: language.isocode,
			label: this.translate.instant('LANGUAGE_' + language.isocode.toUpperCase())
		}));

		// filter other languages already used for this clip
		if (this.entity && this.mediaclip?.subtitles && this.mediaclip.subtitles?.length > 0) {
			const inUse = this.mediaclip.subtitles.map(sub => sub.isocode);
			this.languageOptions = this.languageOptions.filter(option => this.entity.isocode === option.value ? true : !inUse.includes(option.value));
		}

		// set default for new
		if (!this.entity?.isocode && this.isNew && this.languageOptions.length > 0) {
			this.entity.isocode = this.languageOptions[0].value;
		}
	}

	private resetErrors(): void {
		this.errorMessage = '';
		this.errorLineNumbers = [];
	}

	private checkIsValidSrt(): boolean {
		this.resetErrors();

		if (this.entity.srt) {
			const result: { errorCode: string; lineNumber: number; message: string }[] = this.validator(this.entity.srt);

			if (result.length) {
				const errorMessage = result[0].message + ' on line ' + result[0].lineNumber + (result.length > 1 ? ` and ${result.length - 1} other issues` : '');
				// sometimes the validator only returns first error found: created a GH issue https://github.com/taoning2014/srt-validator/issues/42
				this.errorLineNumbers = result.map(r => ({ lineNumber: r.lineNumber, message: r.message }));
				this.errorMessage = errorMessage;

				if (this.currentMode === 'editor') {
					this.overlayService.createToast(this.translate.instant('ERROR_SAVING_SUBTITLE', { errorMessage }));
				}
				return false;
			}
		}
		return true;
	}
}
